import { useRouter } from 'next/router';

import { usePublicViewerHost } from '~/providers/PublicViewerHostProvider';
import { getBoardIdFromPath } from '~/utils/PathUtils';

/**
 * The board id the board-view data layer should scope to.
 *
 * On the public share page and the Preview iframe the id comes from the `/b/{boardId}` URL
 * segment, exactly as `useURLBoardIdSelector` has always returned it. But the collection link
 * editor's Edit tab has no `/b/…` URL, so the URL id is always `undefined` there and the board
 * queries would stay `enabled: false` — nothing would render when the author drills into a board.
 * In the Edit tab the drilled-into board is the editor's `activeBoardId` instead, and that value
 * wins here when present.
 *
 * `usePublicViewerHost()` is null everywhere outside the editor, so on the public page this is
 * byte-identical to `useURLBoardIdSelector` and hydration is unaffected. Resolution mirrors
 * `usePublicShowcaseCurrentSubPage`.
 *
 * Only the board-view DATA hooks that must re-scope to the drilled board read this
 * (useBoardSearchParams and the gallery/table item hooks) — the ~70 other `useURLBoardIdSelector`
 * consumers stay URL-only on purpose, and navigation reads/writes live on the required public
 * contracts instead (the asset modal on PublicAssetModalNavigationProvider, drills on the
 * board/collection trees' own contracts). This selector cannot move onto a public contract because
 * its consumers also serve the PRIVATE app (private gallery/table/kanban), which never mounts the
 * public providers — so it stays on the optional host context, whose `activeBoardId` exists only
 * for it.
 */
export const useCurrentBoardIdSelector = () => {
  const { asPath } = useRouter();
  const host = usePublicViewerHost();

  // `activeBoardId` is `null` when the editor is showing a page body (not drilled into a board);
  // there is never a `/b/…` URL in the Edit tab, so the `??` falls through to `undefined` in that
  // case — the same "no board" answer the URL branch gives on the collection page.
  return host?.navigation?.activeBoardId ?? getBoardIdFromPath(asPath);
};
